import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpBackend, HttpErrorResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { HelpConfig } from "../../config/help-config";
import { LocalStorageService } from "ngx-webstorage";
import { Variables } from "../../utils/variables";
import { Inspection } from "src/app/pages/operacao/vistoria/inspection.service";

interface ILogEntry {
  stream: {
    [key: string]: string;
  };
  values: Array<[string, string]>;
}

interface ILokiPayload {
  streams: ILogEntry[];
}
export type StatusVistoriaType = "APROVADO" | "REPROVADO";

export type levelInfo = "error" | "info" | "warn" | "debug"

export interface IProcessingTimeMetric {
  operation: string;
  duration: number;
  endpoint?: string;
  success: boolean;
}

export interface IBaseInspectionMetric {
  error?: HttpErrorResponse;
  type: levelInfo;
  action: string;
  os: string;
}

export interface IInspectionMetric extends IBaseInspectionMetric {
  routeId: string;
  action: "create_inspecao" | "update_inspecao" | "delete_inspecao";
  inspectionId?: string;
  inspection?: Inspection

}
export interface IVistoriaMetric extends IBaseInspectionMetric {
  action: "create_vistoria" | "update_vistoria" | "delete_vistoria";
  inspectionId?: string;
  inspection?: Inspection
  status: StatusVistoriaType
}

export interface IPagamentoMetric extends IBaseInspectionMetric {
  routeId: string;
  action: "create_pagamento" | "update_pagamento" | "delete_pagamento";
  inspection?: unknown;
  inspectionId?: string;
}

export interface IVolumeMetric {
  labels: { [key: string]: string };
  operation: string;
  count?: number;
  message: string;
}
export interface IVolumeMetricParams extends Pick<IVolumeMetric, "operation"> {}

export interface IVistoriaVolumeMetric extends IVolumeMetricParams {
  status: StatusVistoriaType,
  evcName: string,
}

export interface IPagamentoVolumeMetric extends IVolumeMetricParams {
  evcName: string,
}

export interface IErrorDataResponse {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
}
export interface IErrorResponse {
  data: IErrorDataResponse;
  message: string;
  severity: string;
  success: boolean;
}


@Injectable({
  providedIn: "root",
})
export class LokiMonitoringService {
  private httpClient: HttpClient;
  private httpBackend = inject(HttpBackend);
  private helpConfig = inject(HelpConfig);
  private batchQueue: ILogEntry[] = [];
  private batchSize = 10;
  private flushInterval = 5000;
  private flushTimer?: ReturnType<typeof setInterval>;
  private readonly _storageService = inject(LocalStorageService)

  constructor() {
    this.httpClient = new HttpClient(this.httpBackend);

    if (typeof window !== "undefined") {
      this.startBatchFlush();
    }
  }

  private sendToLoki(logs: ILogEntry[]): Observable<unknown> {
    if (logs.length === 0) {
      return of(null);
    }

    if (!this.httpClient) {
      return of(null);
    }

    const payload: ILokiPayload = {
      streams: logs,
    };

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    const lokiUrl = this.helpConfig.LOKI_URL;
    if (!lokiUrl) {
      return of(null);
    }

    return this.httpClient.post(lokiUrl, payload, { headers }).pipe(
      catchError(() => of(null))
    );
  }

  private createLogEntry(
    level: levelInfo,
    message: string,
    labels: { [key: string]: string } = {},
    additionalData: Record<string, unknown> = {}
  ): ILogEntry {
    let user: { user: string } | null = null;

    try {
      user = this._storageService.retrieve(Variables.USER_LOGIN) || null;
    } catch {
      user = null;
    }

    const streamLabels: { [key: string]: string } = {
      level,
      app: "inspection-client",
      environment: this.getEnvironment(),
      ...labels,
    };

    if (user?.user) {
      streamLabels["userId"] = String(user.user);
    }

    const logData: Record<string, unknown> = {
      message,
      timestamp: new Date().toISOString(),
      ...additionalData,
    };

    if (user?.user && !logData["userId"]) {
      logData["userId"] = String(user.user);
    }

    const logLine = JSON.stringify(logData);
    const timestamp = Date.now().toString() + "000000";

    return {
      stream: streamLabels,
      values: [[timestamp, logLine]],
    };
  }

  private queueLog(entry: ILogEntry): void {
    this.batchQueue.push(entry);

    if (this.batchQueue.length >= this.batchSize) {
      this.flushBatch();
    }
  }

  private flushBatch(): void {
    if (this.batchQueue.length === 0) {
      return;
    }

    const logsToSend = [...this.batchQueue];
    this.batchQueue = [];

    this.sendToLoki(logsToSend).subscribe();
  }

  private startBatchFlush(): void {
    if (typeof window !== "undefined" && typeof setInterval !== "undefined") {
      this.flushTimer = setInterval(() => {
        this.flushBatch();
      }, this.flushInterval);
    }
  }

  /**
   * Para o timer de envio em lote
   */
  public stopBatchFlush(): void {
    if (this.flushTimer && typeof clearInterval !== "undefined") {
      clearInterval(this.flushTimer);
      this.flushTimer = undefined;
    }
    this.flushBatch();
  }

  private getEnvironment(): string {
    const configEnvironment = this.helpConfig.MONITORING_ENVIRONMENT;
    if (configEnvironment) {
      return configEnvironment;
    }
    const hostname = window.location.hostname;
    if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
      return "local";
    }
    if (hostname.includes("hmg") || hostname.includes("homolog")) {
      return "homologation";
    }
    if (hostname.includes("stage") || hostname.includes("staging")) {
      return "staging";
    }
    if (hostname.includes("prod") || hostname.includes("production")) {
      return "production";
    }
    if (hostname.includes("dev")) {
      return "development";
    }
    return "unknown";
  }

  /**
   * Registra métrica de tempo de processamento
   */
  logProcessingTime(metric: IProcessingTimeMetric): void {
    const entry = this.createLogEntry(
      "info",
      `Tempo de processamento: ${metric.operation} - ${metric.duration}ms`,
      {
        metric: "processing_time",
        operation: metric.operation,
        success: metric.success.toString(),
      },
      {
        operation: metric.operation,
        duration: metric.duration,
        endpoint: metric.endpoint,
        success: metric.success,
      }
    );

    this.queueLog(entry);
  }

  getRegisterAction(type: levelInfo, title: string): string {
    return `${type}_${title.toLowerCase()}`;
  }

  /**
   * Registra métrica de formulário ECV
   */
  logRegisterVistoriaMetric(vistoriaMetric: IInspectionMetric): void {
    const typeMetric = vistoriaMetric.type
    const error = vistoriaMetric.error ? vistoriaMetric.error.error : null
    const message = this.getRegisterAction(vistoriaMetric.type, vistoriaMetric.action);

    const labels: { [key: string]: string } = {
      metric: "register_vistoria",
      routeId: vistoriaMetric.routeId,
      action: vistoriaMetric.action,
    };

    if (typeMetric === "error" && error) {
      this.getDefaultErrorLabels(error, labels)
    }

    const additionalData: Record<string, unknown> = {
      payload: this.toSafeInspectionPayload(vistoriaMetric.inspection),
      type: vistoriaMetric.type,
      os: vistoriaMetric.os,
      inspectionId: vistoriaMetric.inspectionId,
    };

    const entry = this.createLogEntry(
      typeMetric,
      message,
      labels,
      additionalData
    );

    this.queueLog(entry);
  }

  logRegisterLaudoMetric(vistoriaMetric: IVistoriaMetric): void {
    const typeMetric = vistoriaMetric.type
    const error = vistoriaMetric.error ? vistoriaMetric.error : null
    const errorMessage = `${vistoriaMetric.action} - ${error?.error.title}`
    const message = this.getRegisterAction(vistoriaMetric.type, errorMessage);

    const labels: { [key: string]: string } = {
      metric: "register_laudo",
      action: vistoriaMetric.action,
    };

    if (typeMetric === "error" && error) {
      this.getDefaultErrorLabels(error, labels)
    }

    const additionalData: Record<string, unknown> = this.getDefaultAdditionalData(vistoriaMetric, error)

    const entry = this.createLogEntry(
      typeMetric,
      message,
      labels,
      additionalData
    );

    this.queueLog(entry);
  }

  private getDefaultErrorLabels(error: HttpErrorResponse, labels: { [key: string]: string }){
      labels["error"] = error.message;
      labels["stack"] = error.error?.stack || "No stack trace";
      labels["instance"] = this.stripQueryString(error.url ?? "");
  }

  private getDefaultAdditionalData(
    data: {
      os?: string;
      inspection?: unknown;
      status?: unknown;
      type?: unknown;
      inspectionId?: string;
    },
    error: HttpErrorResponse | null
  ){
    const errorsList = error?.error?.errors
    const errorTitle = error?.error?.title
    const pageUrl =
      typeof window !== "undefined" ? window.location.href : "unknown";

    const defaultData = {
      errors: errorsList?.[errorTitle ?? ''],
      instance: this.stripQueryString(error?.url),
      os: data.os,
      payload: this.toSafeInspectionPayload(data.inspection as Inspection | null | undefined),
      status: data.status,
      type: data.type,
      inspectionId: data.inspectionId,
      url: this.stripQueryString(pageUrl),
    }
    return defaultData
  }

  private toSafeInspectionPayload(inspection: Inspection | null | undefined): Record<string, unknown> | null {
    if (!inspection) {
      return null;
    }
    return {
      id: inspection.id,
      serviceOrderNumber: inspection.serviceOrderNumber,
      idStatusInspection: inspection.idStatusInspection,
      statusInspectionName: inspection.statusInspectionName,
      statusType: inspection.statusType,
      idTaskType: inspection.idTaskType,
      idPlateRequest: inspection.idPlateRequest,
      sealed: inspection.sealed,
      requiresECV: inspection.requiresECV,
    };
  }

  private stripQueryString(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    return url.split("?")[0];
  }

  logRegisterPagamentoMetric(pagamentoMetric: IPagamentoMetric): void {
    const typeMetric = pagamentoMetric.type
    const error = pagamentoMetric.error ?? null
    const errorTitle = error?.error.title
    const errorMessage = `${pagamentoMetric.action} - ${errorTitle}`

    const message = this.getRegisterAction(pagamentoMetric.type, errorMessage);

    const labels: { [key: string]: string } = {
      metric: "register_pagamento",
      routeId: pagamentoMetric.routeId,
      action: pagamentoMetric.action,
      os: pagamentoMetric.os,
    };

    if (typeMetric === "error" && error) {
      this.getDefaultErrorLabels(error, labels)
    }

    const dafaultAdditionalData: Record<string, unknown> = this.getDefaultAdditionalData(pagamentoMetric, error)

    const additionalData = {
      ...dafaultAdditionalData,
      routeId: pagamentoMetric.routeId,
    }

    const entry = this.createLogEntry(
      typeMetric,
      message,
      labels,
      additionalData
    );

    this.queueLog(entry);
  }

  /**
   * Registra métrica de volume por clínica
   */
  logVolumeMetric(metric: IVolumeMetric): void {
    const labels: { [key: string]: string } = {
      ...metric.labels,
      metric: "volume",
    };

    const entry = this.createLogEntry(
      "info",
      metric.message,
      labels,
      {
        operation: metric.operation,
        count: metric.count ?? 1,
      }
    );

    this.queueLog(entry);
  }

  logVolumeVistoriaMetric(metric: IVistoriaVolumeMetric) : void {
    const labels: { [key: string]: string } = {
      operation: metric.operation,
      ecvName: metric.evcName,
      status: metric.status
    };

    const message =  `Volume por ecv: ${metric.evcName} - ${metric.operation}`

    const volumeMetric: IVolumeMetric = {
      labels: labels,
      message: message,
      operation: metric.operation
    };

    this.logVolumeMetric(volumeMetric)
  }

  logVolumePagamentoMetric(metric: IPagamentoVolumeMetric) : void {
    const labels: { [key: string]: string } = {
      operation: metric.operation,
      ecvName: metric.evcName,
    };

    const message =  `Volume por ecv: ${metric.evcName} - ${metric.operation}`

    const volumeMetric: IVolumeMetric = {
      labels: labels,
      message: message,
      operation: metric.operation
    };

    this.logVolumeMetric(volumeMetric)
  }

  /**
   * Registra log genérico
   */
  log(
    level: levelInfo,
    message: string,
    data?: Record<string, unknown>
  ): void {
    const entry = this.createLogEntry(level, message, {}, data || {});
    this.queueLog(entry);
  }

  getErrorMessage(error: unknown): string | object {
    const errorType = this.getErrorType(error);

    switch (errorType) {
      case "http":
        return this.formatHttpError(error as HttpErrorResponse);

      case "native":
        return this.formatNativeError(error as Error);

      case "object":
        return this.formatErrorObject(error as object);

      case "primitive":
        return this.formatPrimitiveError(error);

      default:
        return "Erro desconhecido";
    }
  }

  private getErrorType(error: unknown): "http" | "native" | "object" | "primitive" | "unknown" {
    if (error instanceof HttpErrorResponse) return "http";
    if (error instanceof Error) return "native";
    if (typeof error === "object" && error !== null) return "object";
    if (typeof error === "string" || typeof error === "number" || typeof error === "boolean")
      return "primitive";
    return "unknown";
  }

  isErrorResponseType(error: object): error is IErrorResponse {
    const isTypeIError = "data" in error;
    return isTypeIError;
  }

  private formatErrorObject(error: IErrorResponse | object): string | object {
    let errorMessage: string | object = "";

    if (this.isErrorResponseType(error)) {
      errorMessage = error.data;
    } else {
      errorMessage = error;
    }

    return errorMessage;
  }

  private formatHttpError(error: HttpErrorResponse): string {
    const baseMessage = `HTTP ${error.status} - ${error.statusText}`;
    const errorDetail = error.error;
    return errorDetail ? `${baseMessage}: ${errorDetail}` : baseMessage;
  }

  private formatNativeError(error: Error): string {
    return `${error.name}: ${error.message}`;
  }

  private formatPrimitiveError(error: unknown): string {
    return String(error);
  }

  logTypeScriptError(error: unknown): void {
    const message = this.getErrorMessage(error);
    const stack = error instanceof Error ? error.stack : "No stack trace";

    const entry = this.createLogEntry(
      "error",
      `Erro de Aplicação: ${message}`,
      {
        metric: "typescript_error",
        error_name: error instanceof Error ? error.name : "Error",
      },
      {
        message,
        stack,
        url: this.stripQueryString(
          typeof window !== "undefined" ? window.location.href : "unknown"
        ),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      }
    );

    this.queueLog(entry);
  }

  /**
   * Força o envio imediato de todos os logs pendentes
   */
  flush(): void {
    this.flushBatch();
  }
}
